import { useMemo, useState } from 'react';
import { MapPinPlus } from 'lucide-react';
import type { LocationKind } from '@vtak/shared';
import { useMapStore } from '@/stores/map-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useCreateLocation } from '@/queries/use-location-queries';
import { IconPicker } from '@/components/common/icon-picker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/** Kinds any member may place (unit-shared field reports + personal bookmark). */
const MEMBER_KINDS: ReadonlyMap<LocationKind, string> = new Map([
  ['mission', 'Mission'],
  ['hazard', 'Hazard'],
  ['roadblock', 'Roadblock'],
  ['waypoint', 'Waypoint'],
  ['custom', 'Custom'],
  ['saved', 'Saved (personal)'],
]);

/** Additional kinds an admin may place (base infrastructure). */
const ADMIN_KINDS: ReadonlyMap<LocationKind, string> = new Map([
  ['gate', 'Gate'],
  ['pass-id', 'Pass & ID'],
  ['fire-station', 'Fire Station'],
  ['facility', 'Facility'],
  ['landmark', 'Landmark'],
]);

/** Default marker glyph per placeable kind (pre-selected in the picker). */
const DEFAULT_ICON_BY_KIND: ReadonlyMap<LocationKind, string> = new Map([
  ['mission', 'objective'],
  ['hazard', 'hazard'],
  ['roadblock', 'roadblock'],
  ['waypoint', 'waypoint'],
  ['custom', 'location'],
]);

/** The kinds the caller may place, per role. */
function placeableKinds(isAdmin: boolean): Map<LocationKind, string> {
  const kinds = new Map(MEMBER_KINDS);
  if (isAdmin) for (const [kind, label] of ADMIN_KINDS) kinds.set(kind, label);
  return kinds;
}

/** Coordinate for the new pin: last map click, else the unit's default view. */
function placementCoords(): { lat: number; lng: number } {
  const click = useMapStore.getState().lastClick;
  if (click) return { lat: click.lat, lng: click.lng };
  const view = useWorkspaceStore.getState().workspace?.mapView;
  return { lat: view?.lat ?? 0, lng: view?.lng ?? 0 };
}

/**
 * "Add location" form: click the map to set the spot, then name it, pick a
 * kind and a marker glyph. Field-report kinds are member-placeable and shared
 * with the unit; infrastructure kinds appear for admins only.
 */
export function AddLocationForm({
  unitId,
  isAdmin,
}: {
  unitId: string;
  isAdmin: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<LocationKind>('mission');
  const [icon, setIcon] = useState<string | undefined>(DEFAULT_ICON_BY_KIND.get('mission'));
  const lastClick = useMapStore((s) => s.lastClick);
  const create = useCreateLocation(unitId);

  const kinds = useMemo(() => placeableKinds(isAdmin), [isAdmin]);

  function selectKind(next: LocationKind): void {
    setKind(next);
    const defaultIcon = DEFAULT_ICON_BY_KIND.get(next);
    if (defaultIcon) setIcon(defaultIcon);
  }

  function save(): void {
    const trimmed = name.trim();
    if (!trimmed) return;
    const at = placementCoords();
    create.mutate(
      { name: trimmed, kind, lat: at.lat, lng: at.lng, zoom: 14, ...(icon ? { icon } : {}) },
      { onSuccess: () => { setName(''); setOpen(false); } },
    );
  }

  if (!open) {
    return (
      <Button size="sm" variant="outline" className="w-full gap-1.5" onClick={() => setOpen(true)}>
        <MapPinPlus className="h-3.5 w-3.5" /> Add location
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[11px] text-muted-foreground">
        {lastClick
          ? `Placing at ${lastClick.lat.toFixed(4)}, ${lastClick.lng.toFixed(4)} (last map click)`
          : 'Click the map to choose the spot, or the unit default view is used.'}
      </p>
      <Input
        autoFocus
        value={name}
        placeholder="Location name"
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setOpen(false); }}
      />
      <div className="flex flex-wrap gap-1">
        {[...kinds.entries()].map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => selectKind(value)}
            className={
              value === kind
                ? 'rounded-full border border-echo-yellow/50 bg-echo-yellow/15 px-2.5 py-1 text-[11px] text-echo-yellow'
                : 'rounded-full border border-echo-border px-2.5 py-1 text-[11px] text-muted-foreground hover:text-foreground'
            }
          >
            {label}
          </button>
        ))}
      </div>
      <IconPicker value={icon} onChange={setIcon} />
      <div className="flex gap-2">
        <Button size="sm" className="flex-1" disabled={!name.trim() || create.isPending} onClick={save}>
          {create.isPending ? 'Adding…' : 'Add location'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}
